'use client';

import { c as _c } from "react/compiler-runtime";
import { useAuth } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { UserAvatar } from '@/components/images/user-avatar';
import { ACCOUNT_PILL_CLASS, AccountPillBody } from '@/components/ui/account-pill';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/app-dropdown-menu';
import { Icon } from '@/components/ui/icon';
import { BubbleChatQuestionIcon, Bug01Icon, ChartRoseIconSolid, DashboardSquare01Icon, Logout01Icon, User03Icon } from '@/components/ui/icon/glyphs';
import { onEvent } from '@/events';
import { useMediaQuery } from '@/hooks/ui/use-media-query';
import { Link } from '@/i18n/navigation';
import { NetworkLink } from '@/i18n/network-link';
import { markLogoutIntent } from '@/lib/auth/logout-intent';
import { cn } from '@/lib/ui/utils';
import { useLayoutStore } from '@/stores/layout';
import { openSupport } from '@/stores/support';
interface RewardsUserMenuProps {
  points: string;
  handle: string | null;
  userId: string | null;
  avatarUrl: string | null;
  /** Which rewards view is currently shown — drives the active menu highlight. */
  active: 'dashboard' | 'profile';
}

/** Close delay (ms) so the pointer can cross the gap from trigger to content. */
const CLOSE_DELAY_MS = 120;
function preventAutoFocus(e: Event): void {
  e.preventDefault();
}

/**
 * Hover-to-open state for the menu (canonical recipe — see the
 * morfi-hover-dropdown skill + wallet-dropdown.tsx). Requires `modal={false}`
 * on the DropdownMenu and `onCloseAutoFocus` prevention on the content.
 */
function useHoverDropdown() {
  const $ = _c(12);
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const isCoarsePointer = useMediaQuery("(pointer: coarse)");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = function clearTimer() {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const clearTimer = t0;
  let t1;
  if ($[1] !== isCoarsePointer) {
    t1 = function startCloseTimer() {
      if (isCoarsePointer) {
        return;
      }
      clearTimer();
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        setOpen(false);
      }, CLOSE_DELAY_MS);
    };
    $[1] = isCoarsePointer;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const startCloseTimer = t1;
  let t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
    t3 = [];
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== isCoarsePointer) {
    t4 = next => {
      if (!isCoarsePointer && next) {
        return;
      }
      setOpen(next);
    };
    $[5] = isCoarsePointer;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      clearTimer();
      setOpen(true);
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== open || $[9] !== startCloseTimer || $[10] !== t4) {
    t6 = {
      open,
      onOpenChange: t4,
      onTriggerEnter: t5,
      onTriggerLeave: startCloseTimer,
      onContentEnter: clearTimer,
      onContentLeave: startCloseTimer
    };
    $[8] = open;
    $[9] = startCloseTimer;
    $[10] = t4;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}

/**
 * Top-right account control on the rewards dashboard: the points pill + profile
 * avatar are a hover-opened dropdown. "Sign out" tears down the session via the
 * SDK auth context.
 */
export function RewardsUserMenu(t0) {
  const $ = _c(90);
  const {
    points,
    handle,
    userId,
    avatarUrl,
    active
  } = t0;
  const t = useTranslations("rewards");
  const {
    logout
  } = useAuth();
  const openFeedback = useLayoutStore(_temp);
  const hover = useHoverDropdown();
  const displayLabel = handle;
  const t1 = hover.open;
  const t2 = hover.onOpenChange;
  let t3;
  if ($[0] !== t) {
    t3 = t("userMenu.accountMenu");
    $[0] = t;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = hover.onTriggerEnter;
  const t5 = hover.onTriggerLeave;
  let t6;
  if ($[2] !== points || $[3] !== t) {
    t6 = t("userMenu.points", {
      points
    });
    $[2] = points;
    $[3] = t;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== t6) {
    t7 = <span data-testid="points-current" className="text-caption font-medium font-mono tabular-nums text-foreground-brand">{t6}</span>;
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== avatarUrl || $[8] !== displayLabel || $[9] !== userId) {
    t8 = <UserAvatar userId={userId} displayName={displayLabel} avatarUrl={avatarUrl} size="xs" nsfw={false} rasterise={false} className="shrink-0" />;
    $[7] = avatarUrl;
    $[8] = displayLabel;
    $[9] = userId;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t7 || $[12] !== t8) {
    t9 = <AccountPillBody>{t7}{t8}</AccountPillBody>;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== hover.onTriggerEnter || $[15] !== hover.onTriggerLeave || $[16] !== t3 || $[17] !== t9) {
    t10 = <DropdownMenuTrigger className={ACCOUNT_PILL_CLASS} data-testid="rewards-user-menu-trigger" aria-label={t3} onPointerEnter={t4} onPointerLeave={t5}>{t9}</DropdownMenuTrigger>;
    $[14] = hover.onTriggerEnter;
    $[15] = hover.onTriggerLeave;
    $[16] = t3;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const t11 = hover.onContentEnter;
  const t12 = hover.onContentLeave;
  let t13;
  if ($[19] !== avatarUrl || $[20] !== displayLabel || $[21] !== userId) {
    t13 = <UserAvatar userId={userId} displayName={displayLabel} avatarUrl={avatarUrl} size="sm" nsfw={false} rasterise={false} />;
    $[19] = avatarUrl;
    $[20] = displayLabel;
    $[21] = userId;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const t14 = handle ?? "anon";
  let t15;
  if ($[23] !== t14) {
    t15 = <span className="min-w-0 truncate text-caption font-medium text-foreground-primary">@{t14}</span>;
    $[23] = t14;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t13 || $[26] !== t15) {
    t16 = <div className="flex items-center gap-2.5 px-2 py-1.5">{t13}{t15}</div>;
    $[25] = t13;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  const t17 = active === "dashboard" && "bg-background-tertiary";
  let t18;
  if ($[28] !== t17) {
    t18 = cn(t17);
    $[28] = t17;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Icon icon={DashboardSquare01Icon} className="mr-2 size-4" />;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== t) {
    t20 = t("userMenu.dashboard");
    $[31] = t;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  let t21;
  if ($[33] !== t20) {
    t21 = <Link href="/rewards">{t19}{t20}</Link>;
    $[33] = t20;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  let t22;
  if ($[35] !== t18 || $[36] !== t21) {
    t22 = <DropdownMenuItem asChild={true} data-testid="menu-dashboard" className={t18}>{t21}</DropdownMenuItem>;
    $[35] = t18;
    $[36] = t21;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  const t23 = active === "profile" && "bg-background-tertiary";
  let t24;
  if ($[38] !== t23) {
    t24 = cn(t23);
    $[38] = t23;
    $[39] = t24;
  } else {
    t24 = $[39];
  }
  let t25;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <Icon icon={User03Icon} className="mr-2 size-4" />;
    $[40] = t25;
  } else {
    t25 = $[40];
  }
  let t26;
  if ($[41] !== t) {
    t26 = t("userMenu.profile");
    $[41] = t;
    $[42] = t26;
  } else {
    t26 = $[42];
  }
  let t27;
  if ($[43] !== t26) {
    t27 = <Link href="/rewards?profile">{t25}{t26}</Link>;
    $[43] = t26;
    $[44] = t27;
  } else {
    t27 = $[44];
  }
  let t28;
  if ($[45] !== t24 || $[46] !== t27) {
    t28 = <DropdownMenuItem asChild={true} data-testid="menu-profile" className={t24}>{t27}</DropdownMenuItem>;
    $[45] = t24;
    $[46] = t27;
    $[47] = t28;
  } else {
    t28 = $[47];
  }
  let t29;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <Icon icon={ChartRoseIconSolid} className="mr-2 size-4" />;
    $[48] = t29;
  } else {
    t29 = $[48];
  }
  let t30;
  if ($[49] !== t) {
    t30 = t("userMenu.markets");
    $[49] = t;
    $[50] = t30;
  } else {
    t30 = $[50];
  }
  let t31;
  if ($[51] !== t30) {
    t31 = <DropdownMenuItem asChild={true} data-testid="menu-markets"><NetworkLink href="/markets">{t29}{t30}</NetworkLink></DropdownMenuItem>;
    $[51] = t30;
    $[52] = t31;
  } else {
    t31 = $[52];
  }
  let t32;
  if ($[53] !== openFeedback) {
    t32 = () => {
      onEvent("feedback_opened", {
        source: "rewards_menu",
        intent: "bug"
      });
      openFeedback({
        intent: "bug"
      });
    };
    $[53] = openFeedback;
    $[54] = t32;
  } else {
    t32 = $[54];
  }
  let t33;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <Icon icon={Bug01Icon} className="mr-2 size-4" />;
    $[55] = t33;
  } else {
    t33 = $[55];
  }
  let t34;
  if ($[56] !== t) {
    t34 = t("userMenu.reportBug");
    $[56] = t;
    $[57] = t34;
  } else {
    t34 = $[57];
  }
  let t35;
  if ($[58] !== t32 || $[59] !== t34) {
    t35 = <DropdownMenuItem data-testid="menu-report-bug" onSelect={t32}>{t33}{t34}</DropdownMenuItem>;
    $[58] = t32;
    $[59] = t34;
    $[60] = t35;
  } else {
    t35 = $[60];
  }
  let t36;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <Icon icon={BubbleChatQuestionIcon} className="mr-2 size-4" />;
    $[61] = t36;
  } else {
    t36 = $[61];
  }
  let t37;
  if ($[62] !== t) {
    t37 = t("userMenu.support");
    $[62] = t;
    $[63] = t37;
  } else {
    t37 = $[63];
  }
  let t38;
  if ($[64] !== t37) {
    t38 = <DropdownMenuItem data-testid="menu-support" onSelect={_temp2}>{t36}{t37}</DropdownMenuItem>;
    $[64] = t37;
    $[65] = t38;
  } else {
    t38 = $[65];
  }
  let t39;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <DropdownMenuSeparator />;
    $[66] = t39;
  } else {
    t39 = $[66];
  }
  let t40;
  if ($[67] !== logout) {
    t40 = () => {
      markLogoutIntent();
      logout();
    };
    $[67] = logout;
    $[68] = t40;
  } else {
    t40 = $[68];
  }
  let t41;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <Icon icon={Logout01Icon} className="mr-2 size-4" />;
    $[69] = t41;
  } else {
    t41 = $[69];
  }
  let t42;
  if ($[70] !== t) {
    t42 = t("userMenu.signOut");
    $[70] = t;
    $[71] = t42;
  } else {
    t42 = $[71];
  }
  let t43;
  if ($[72] !== t40 || $[73] !== t42) {
    t43 = <DropdownMenuItem onSelect={t40}>{t41}{t42}</DropdownMenuItem>;
    $[72] = t40;
    $[73] = t42;
    $[74] = t43;
  } else {
    t43 = $[74];
  }
  let t44;
  if ($[75] !== hover.onContentEnter || $[76] !== hover.onContentLeave || $[77] !== t16 || $[78] !== t22 || $[79] !== t28 || $[80] !== t31 || $[81] !== t35 || $[82] !== t38 || $[83] !== t43) {
    t44 = <DropdownMenuContent align="end" sideOffset={6} className="flex w-56 flex-col gap-1" onPointerEnter={t11} onPointerLeave={t12} onCloseAutoFocus={preventAutoFocus}>{t16}{t22}{t28}{t31}{t35}{t38}{t39}{t43}</DropdownMenuContent>;
    $[75] = hover.onContentEnter;
    $[76] = hover.onContentLeave;
    $[77] = t16;
    $[78] = t22;
    $[79] = t28;
    $[80] = t31;
    $[81] = t35;
    $[82] = t38;
    $[83] = t43;
    $[84] = t44;
  } else {
    t44 = $[84];
  }
  let t45;
  if ($[85] !== hover.onOpenChange || $[86] !== hover.open || $[87] !== t10 || $[88] !== t44) {
    t45 = <DropdownMenu open={t1} onOpenChange={t2} modal={false}>{t10}{t44}</DropdownMenu>;
    $[85] = hover.onOpenChange;
    $[86] = hover.open;
    $[87] = t10;
    $[88] = t44;
    $[89] = t45;
  } else {
    t45 = $[89];
  }
  return t45;
}
function _temp2() {
  openSupport();
}
function _temp(s) {
  return s.openFeedback;
}